if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        this.hasGalleryInteraction = false;
        this.galleryInteractionBound = false;
        this.handleGalleryInteractionBound = this.handleGalleryInteraction.bind(this);
        this.handleDeferredImageIntersection = this.handleDeferredImageIntersection.bind(this);
        this.onSlideChangedHandler = debounce(this.onSlideChanged.bind(this), 120);
        this.variantMediaData = this.getVariantMediaData();

        this.elements.viewer?.addEventListener('slideChanged', this.onSlideChangedHandler);

        if (this.elements.thumbnails) {
          this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            mediaToSwitch
              .querySelector('button')
              .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
          });
          if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
        }

        if (this.variantMediaData) {
          this.applyVariantMediaFilter(this.getCurrentVariantId(), this.getActiveMediaId());
          this.variantMediaUnsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (!data) return;
            const productInfo = this.closest('product-info');
            if (productInfo && data.sectionId && data.sectionId !== productInfo.dataset.section) return;
            this.applyVariantMediaFilter(data.variant?.id ?? this.getCurrentVariantId(), data.variant?.featured_media?.id, {
              fromVariantChange: true,
            });
          });
        }

        this.initMobileMediaSkeletons();
        this.hydrateMediaWindow();
        this.observeDeferredImages();
        this.bindGalleryInteraction();
      }

      disconnectedCallback() {
        this.elements.viewer?.removeEventListener('slideChanged', this.onSlideChangedHandler);
        this.disconnectDeferredImageObservers();
        this.variantMediaUnsubscribe?.();
        this.teardownGalleryInteraction();
      }

      onSlideChanged(event) {
        const activeMedia = event.detail.currentElement;
        if (!activeMedia) return;

        this.setActiveSlide(activeMedia);
        this.refreshMediaState(activeMedia);

        const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${activeMedia.dataset.mediaId}"]`);
        if (thumbnail) this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, options = {}) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        const mediaParent = activeMedia.parentElement;
        if (!mediaParent) return;
        this.setActiveSlide(activeMedia, options);
        this.hydrateSlideImages(activeMedia, { fetchPriority: 'high', loading: 'eager' });
        this.bindSlideImageLoadState(activeMedia, activeMedia.querySelector('.product__media img'));
        if (options?.skipMediaStateRefresh !== 'true' && options?.skipMediaStateRefresh !== true) {
          this.refreshMediaState(activeMedia);
        }

        if (prepend) {
          mediaParent.firstChild !== activeMedia && mediaParent.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            if (activeThumbnail?.parentElement) {
              activeThumbnail.parentElement.firstChild !== activeThumbnail &&
                activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader({ allowAtTop: options?.fromVariantChange === true });
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            mediaParent.scrollTo({
              left: activeMedia.offsetLeft,
              behavior: options?.fromVariantChange === true ? 'instant' : 'auto',
            });
          }
          if (options?.shouldScrollToMedia === false) return;
          const top = this.getMediaScrollTargetTop(activeMedia);
          if (top == null) return;
          window.scrollTo({ top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        if (!activeThumbnail) return;
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveSlide(activeMedia, options = {}) {
        if (!activeMedia) return;
        this.elements.viewer?.querySelectorAll('[data-media-id].is-active').forEach((element) => {
          if (element !== activeMedia) element.classList.remove('is-active');
        });
        activeMedia.classList.add('is-active');
        if (options?.fromVariantChange === true) {
          this.applyVariantTransition(activeMedia);
        }
      }

      refreshMediaState(activeSlide = null) {
        this.hydrateMediaWindow(activeSlide);
        this.initMobileMediaSkeletons();
        this.observeDeferredImages();
      }

      resetGalleryPages() {
        this.elements.viewer?.resetPages?.();
        this.elements.thumbnails?.resetPages?.();
      }

      applyVariantTransition(activeMedia) {
        if (!activeMedia || this.reducedMotionQuery?.matches) return;
        if (!activeMedia.querySelector('img')) return;
        activeMedia.classList.remove('product__media-item--variant-transition');
        window.requestAnimationFrame(() => {
          activeMedia.classList.add('product__media-item--variant-transition');
          activeMedia.addEventListener(
            'animationend',
            () => {
              activeMedia.classList.remove('product__media-item--variant-transition');
            },
            { once: true }
          );
        });
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;
        const thumbnailButton = thumbnail.querySelector('button');
        if (!thumbnailButton) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnailButton.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      disconnectDeferredImageObservers() {
        this.viewerDeferredImageObserver?.disconnect();
        this.thumbnailDeferredImageObserver?.disconnect();
        this.viewerDeferredImageObserver = null;
        this.thumbnailDeferredImageObserver = null;
      }

      observeDeferredImages() {
        this.disconnectDeferredImageObservers();

        if (typeof IntersectionObserver === 'undefined') {
          this.querySelectorAll('img[data-gallery-deferred-image="true"]').forEach((image) =>
            this.hydrateDeferredImage(image)
          );
          return;
        }

        this.viewerDeferredImageObserver = this.observeDeferredImagesInContainer(
          this.elements.viewer?.slider,
          this.elements.viewer?.querySelectorAll('[data-media-id]')
        );
        this.thumbnailDeferredImageObserver = this.isElementVisible(this.elements.thumbnails)
          ? this.observeDeferredImagesInContainer(
              this.elements.thumbnails?.slider,
              this.elements.thumbnails?.querySelectorAll('[data-target]')
            )
          : null;
      }

      observeDeferredImagesInContainer(root, items) {
        if (!root || !items?.length) return null;

        const deferredItems = Array.from(items).filter(
          (item) => !item.hasAttribute('hidden') && item.querySelector('img[data-gallery-deferred-image="true"]')
        );
        if (!deferredItems.length) return null;

        const observer = new IntersectionObserver(this.handleDeferredImageIntersection, {
          root,
          rootMargin: '0px 320px',
          threshold: 0.01,
        });
        deferredItems.forEach((item) => observer.observe(item));
        return observer;
      }

      handleDeferredImageIntersection(entries, observer) {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          this.hydrateSlideImages(entry.target);
          observer.unobserve(entry.target);
        });
      }

      bindGalleryInteraction() {
        if (this.galleryInteractionBound) return;
        this.galleryInteractionBound = true;
        [this.elements.viewer, this.elements.thumbnails].forEach((element) => {
          if (!element) return;
          element.addEventListener('pointerdown', this.handleGalleryInteractionBound);
          element.addEventListener('touchstart', this.handleGalleryInteractionBound, { passive: true });
          element.addEventListener('keydown', this.handleGalleryInteractionBound);
        });
      }

      teardownGalleryInteraction() {
        if (!this.galleryInteractionBound) return;
        this.galleryInteractionBound = false;
        [this.elements.viewer, this.elements.thumbnails].forEach((element) => {
          if (!element) return;
          element.removeEventListener('pointerdown', this.handleGalleryInteractionBound);
          element.removeEventListener('touchstart', this.handleGalleryInteractionBound);
          element.removeEventListener('keydown', this.handleGalleryInteractionBound);
        });
      }

      handleGalleryInteraction() {
        if (this.hasGalleryInteraction) return;
        this.hasGalleryInteraction = true;
        const activeSlide = this.elements.viewer?.querySelector('[data-media-id].is-active');
        this.refreshMediaState(activeSlide);
      }

      isElementVisible(element) {
        return Boolean(element && element.offsetParent !== null && element.getClientRects().length);
      }

      hydrateMediaWindow(activeSlide = null) {
        const slides = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []).filter(
          (slide) => !slide.hasAttribute('hidden')
        );
        if (!slides.length) return;

        const resolvedActiveSlide =
          (activeSlide && !activeSlide.hasAttribute('hidden') && activeSlide) ||
          slides.find((slide) => slide.classList.contains('is-active')) ||
          slides[0];
        const activeIndex = Math.max(slides.indexOf(resolvedActiveSlide), 0);

        const leadingRadius = this.hasGalleryInteraction ? 6 : 4;
        for (let index = activeIndex - 1; index <= activeIndex + leadingRadius; index += 1) {
          const slide = slides[index];
          if (!slide) continue;
          this.hydrateSlideImages(slide, {
            fetchPriority: index === activeIndex ? 'high' : 'low',
            loading: index === activeIndex || (this.hasGalleryInteraction && index > activeIndex) ? 'eager' : undefined,
          });
        }

        if (!this.elements.thumbnails || !this.isElementVisible(this.elements.thumbnails)) return;

        const thumbnailItems = Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
          (thumb) => !thumb.hasAttribute('hidden')
        );
        if (!thumbnailItems.length) return;

        const activeThumbnail =
          thumbnailItems.find((thumb) => thumb.dataset.target === resolvedActiveSlide.dataset.mediaId) ||
          thumbnailItems[activeIndex] ||
          thumbnailItems[0];
        const activeThumbnailIndex = Math.max(thumbnailItems.indexOf(activeThumbnail), 0);

        for (let index = activeThumbnailIndex - 2; index <= activeThumbnailIndex + 2; index += 1) {
          const thumbnail = thumbnailItems[index];
          if (!thumbnail) continue;
          this.hydrateSlideImages(thumbnail, { fetchPriority: 'low' });
        }
      }

      hydrateSlideImages(container, options = {}) {
        if (!container) return;
        container.querySelectorAll('img[data-gallery-deferred-image="true"]').forEach((image) => {
          this.hydrateDeferredImage(image, options);
        });
      }

      hydrateDeferredImage(image, options = {}) {
        if (!image || image.dataset.galleryHydrated === 'true') return;

        const src = image.dataset.src;
        const srcset = image.dataset.srcset;
        const sizes = image.dataset.sizes;
        const loading = options.loading || image.dataset.loading || 'lazy';
        const fetchPriority = options.fetchPriority || image.dataset.fetchpriority || 'low';

        if (sizes) image.setAttribute('sizes', sizes);
        image.setAttribute('loading', loading);
        image.setAttribute('fetchpriority', fetchPriority);
        if (srcset) image.setAttribute('srcset', srcset);
        if (src) image.setAttribute('src', src);
        image.dataset.galleryHydrated = 'true';
        image.removeAttribute('data-gallery-deferred-image');

        const slide = image.closest('[data-media-id], [data-target]');
        this.bindSlideImageLoadState(slide, image);
      }

      getMediaScrollTargetTop(activeMedia) {
        if (!activeMedia) return null;

        const activeMediaRect = activeMedia.getBoundingClientRect();
        if (activeMediaRect.top > -0.5) return null;
        return Math.max(Math.round(window.scrollY + activeMediaRect.top), 0);
      }

      isDeferredImagePending(image) {
        return image?.dataset.galleryDeferredImage === 'true' && image.dataset.galleryHydrated !== 'true';
      }

      bindSlideImageLoadState(slide, image) {
        if (!slide || !image || this.isDeferredImagePending(image)) return;

        const markLoaded = () => {
          slide.classList.add('is-media-loaded');
        };

        if (image.complete && image.naturalWidth > 1) {
          markLoaded();
          return;
        }

        if (image.dataset.galleryLoadStateBound !== 'true') {
          image.dataset.galleryLoadStateBound = 'true';
          image.addEventListener('load', markLoaded, { once: true });
          image.addEventListener('error', markLoaded, { once: true });
        }

        if (typeof image.decode === 'function') {
          image
            .decode()
            .then(markLoaded)
            .catch(() => {
              if (image.complete && image.naturalWidth > 1) markLoaded();
            });
          return;
        }

        window.requestAnimationFrame(() => {
          if (image.complete && image.naturalWidth > 1) markLoaded();
        });
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader(options = {}) {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(
          new CustomEvent('preventHeaderReveal', {
            detail: { duration: 900, allowAtTop: options.allowAtTop === true },
          })
        );
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }

      getVariantMediaData() {
        const productInfo = this.closest('product-info');
        if (!productInfo) return null;
        const mapEl = productInfo.querySelector('[data-variant-media-map]');
        if (!mapEl) return null;
        try {
          return JSON.parse(mapEl.textContent || '{}');
        } catch (error) {
          console.warn('Invalid variant media map JSON', error);
          return null;
        }
      }

      getCurrentVariantId() {
        const productInfo = this.closest('product-info');
        const input = productInfo?.querySelector('input[name="id"]');
        return input ? input.value : null;
      }

      getActiveMediaId() {
        const active = this.elements.viewer?.querySelector('[data-media-id].is-active');
        if (!active) return null;
        return this.extractMediaId(active.dataset.mediaId);
      }

      extractMediaId(value) {
        if (!value) return null;
        const parts = String(value).split('-');
        return parts[parts.length - 1] || null;
      }

      normalizeShopifyImageFilename(filename) {
        if (!filename) return '';
        return String(filename).toLowerCase().replace(/_(?:\d+x\d*|\d*x\d+|\d+x)(?=\.[^.]+$)/i, '');
      }

      normalizeMediaUrl(value) {
        if (!value) return null;
        let pathname = '';
        try {
          pathname = new URL(String(value), window.location.origin).pathname || '';
        } catch (error) {
          pathname = String(value)
            .split('#')[0]
            .split('?')[0]
            .replace(/^https?:\/\/[^/]+/i, '')
            .replace(/^\/\/[^/]+/i, '');
        }
        if (!pathname) return null;
        try {
          pathname = decodeURIComponent(pathname);
        } catch (error) {
          // Keep original pathname when decode fails.
        }
        pathname = pathname.toLowerCase();
        const filename = pathname.split('/').pop() || '';
        const normalizedFilename = this.normalizeShopifyImageFilename(filename);
        return { pathname, filename, normalizedFilename };
      }

      getSlideMediaUrl(slide) {
        if (!slide) return null;
        const mediaElement = slide.querySelector(
          '.product__media img[src], .product__media source[src], .product__media video[src], .product__media iframe[src], img[src], source[src], video[src], iframe[src]'
        );
        if (!mediaElement) return null;
        return (
          mediaElement.currentSrc ||
          mediaElement.getAttribute('src') ||
          mediaElement.src ||
          mediaElement.getAttribute('poster')
        );
      }

      applyVariantMediaFilter(variantId, featuredMediaId, options = {}) {
        if (!this.variantMediaData || !this.elements.viewer) return;
        const {
          variantMedia = {},
          variantMediaUrls = {},
          variantMediaFallback = [],
          useHideVariants = false,
          otherImagesBehavior = 'hide',
        } = this.variantMediaData;
        const featuredMediaIdString = featuredMediaId ? String(featuredMediaId) : '';
        const appendOtherImagesToEnd = otherImagesBehavior === 'append_end';
        const ids = variantId ? variantMedia[String(variantId)] || [] : [];
        const urls = variantId ? variantMediaUrls[String(variantId)] || [] : [];
        const activeSet = new Set(ids.map((id) => String(id)));
        const fallbackSet = new Set((variantMediaFallback || []).map((id) => String(id)));

        const slides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const thumbs = this.elements.thumbnails
          ? Array.from(this.elements.thumbnails.querySelectorAll('[data-target]'))
          : [];
        const urlPathSet = new Set();
        const urlFilenameSet = new Set();
        (urls || []).forEach((url) => {
          const normalized = this.normalizeMediaUrl(url);
          if (!normalized) return;
          if (normalized.pathname) urlPathSet.add(normalized.pathname);
          if (normalized.filename) urlFilenameSet.add(normalized.filename);
          if (normalized.normalizedFilename) urlFilenameSet.add(normalized.normalizedFilename);
        });

        const urlMatchedSet = new Set();
        if (urlPathSet.size > 0 || urlFilenameSet.size > 0) {
          slides.forEach((slide) => {
            const mediaId = this.extractMediaId(slide.dataset.mediaId);
            if (!mediaId) return;
            const normalizedSlideUrl = this.normalizeMediaUrl(this.getSlideMediaUrl(slide));
            if (!normalizedSlideUrl) return;
            if (
              urlPathSet.has(normalizedSlideUrl.pathname) ||
              urlFilenameSet.has(normalizedSlideUrl.filename) ||
              urlFilenameSet.has(normalizedSlideUrl.normalizedFilename)
            ) {
              urlMatchedSet.add(mediaId);
            }
          });
        }

        const shouldFilter = activeSet.size > 0 || urlMatchedSet.size > 0;

        const isPreferred = (mediaId) => {
          if (!mediaId) return false;
          if (featuredMediaIdString && mediaId === featuredMediaIdString) return true;
          if (shouldFilter) return activeSet.has(mediaId) || urlMatchedSet.has(mediaId);
          if (useHideVariants && fallbackSet.size > 0) {
            return !fallbackSet.has(mediaId);
          }
          return true;
        };

        const isVisible = (mediaId) => {
          if (appendOtherImagesToEnd) return true;
          return isPreferred(mediaId);
        };

        slides.forEach((slide) => {
          const mediaId = this.extractMediaId(slide.dataset.mediaId);
          slide.toggleAttribute('hidden', !isVisible(mediaId));
        });

        thumbs.forEach((thumb) => {
          const mediaId = this.extractMediaId(thumb.dataset.target);
          thumb.toggleAttribute('hidden', !isVisible(mediaId));
        });

        if (appendOtherImagesToEnd) {
          this.reorderElementsByPreference(slides, (slide) =>
            isPreferred(this.extractMediaId(slide.dataset.mediaId))
          );
          this.reorderElementsByPreference(thumbs, (thumb) =>
            isPreferred(this.extractMediaId(thumb.dataset.target))
          );
        }

        const orderedSlides = Array.from(this.elements.viewer.querySelectorAll('[data-media-id]'));
        const visibleSlides = orderedSlides.filter((slide) => !slide.hasAttribute('hidden'));
        if (visibleSlides.length === 0) return;

        const currentActive = orderedSlides.find((slide) => slide.classList.contains('is-active'));
        if (currentActive && !currentActive.hasAttribute('hidden')) {
          this.resetGalleryPages();
          this.refreshMediaState(currentActive);
          return;
        }

        const target = visibleSlides.find(
          (slide) => this.extractMediaId(slide.dataset.mediaId) === String(featuredMediaId)
        ) || visibleSlides[0];
        if (target) {
          this.setActiveMedia(target.dataset.mediaId, false, {
            skipMediaStateRefresh: true,
            fromVariantChange: options.fromVariantChange === true,
          });
        }

        this.resetGalleryPages();
        this.refreshMediaState(target);
      }

      reorderElementsByPreference(elements, isPreferred) {
        if (!elements || elements.length < 2 || typeof isPreferred !== 'function') return;
        const parent = elements[0]?.parentElement;
        if (!parent) return;

        const preferredElements = [];
        const otherElements = [];
        elements.forEach((element) => {
          if (isPreferred(element)) {
            preferredElements.push(element);
          } else {
            otherElements.push(element);
          }
        });

        const orderedElements = preferredElements.concat(otherElements);
        let hasOrderChanged = false;
        orderedElements.forEach((element, index) => {
          if (parent.children[index] !== element) {
            hasOrderChanged = true;
          }
        });
        if (!hasOrderChanged) return;

        orderedElements.forEach((element) => parent.appendChild(element));
      }

      initMobileMediaSkeletons() {
        const skeletonSlides = this.querySelectorAll('.product__media-item--mobile-skeleton');
        if (!skeletonSlides.length) return;

        skeletonSlides.forEach((slide) => {
          const image = slide.querySelector('.product__media img');
          if (!image) {
            slide.classList.add('is-media-loaded');
            return;
          }
          if (this.isDeferredImagePending(image)) return;
          this.bindSlideImageLoadState(slide, image);
        });
      }
    }
  );
}
